/**
 * FlashpointSkillEffect — Fire tree backdraft-path evolution: smoke + fire detonation.
 *
 * Replaces Backdraft. Same detonation mechanic — find the nearest detonatable
 * zone within interaction range and blast all enemies inside it — but Flashpoint
 * can target BOTH smoke clouds and fire zones (Cultivate zones, Immolate trail
 * zones, Shape fire walls, etc.).
 *
 * Damage formulas:
 *  - smoke_cloud: 10 + 15 × ageRatio   (inherited Backdraft formula)
 *  - fire_zone:   dotIntensity × remainingTicks × 1.5
 *    where remainingTicks = Math.floor((durationMs − elapsedMs) / tickIntervalMs)
 *
 * Inherited upgrades (resolver scope 'backdraft'):
 *  - Overpressure: knockbackTiles + staggerDurationMs on blast enemies
 *  - Secondary Burn: spawns a fresh smoke_cloud at detonation site
 *
 * Chain Ignition (`backdraft_path_passive`):
 *  resolver.hasEffect('backdraft', 'detonationDotRefresh') → for each burning
 *  enemy in blast, refresh burning to full defaultBurningDurationMs with
 *  sourceSkillId 'chain_ignition'.
 *
 * @see fire-spec.md § Flashpoint, § Chain Ignition
 * @see ADR-064 (shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { BackdraftZoneCreateConfig } from './BackdraftSkillEffect.js';

// ---------------------------------------------------------------------------
// Zone info — extends BackdraftZoneInfo with optional fire-zone fields
// ---------------------------------------------------------------------------

/**
 * Zone snapshot used by FlashpointSkillEffect.
 * Fire zones additionally carry dotIntensity and tickIntervalMs for the
 * fire zone detonation damage formula.
 */
export interface FlashpointZoneInfo {
  readonly id: string;
  readonly type: string;
  readonly position: { readonly x: number; readonly y: number };
  /** Time already elapsed since zone creation (ms). */
  readonly elapsedMs: number;
  /** Total configured duration of the zone (ms). */
  readonly durationMs: number;
  /** Radius in tiles — used as the explosion blast radius. */
  readonly radiusTiles: number;
  /**
   * Fire zone only: DoT intensity per tick (damage per tick per stack).
   * Required when type === 'fire_zone'. Absent for smoke_cloud.
   */
  readonly dotIntensity?: number;
  /**
   * Fire zone only: tick interval in ms. Used to compute remainingTicks.
   * Required when type === 'fire_zone'. Absent for smoke_cloud.
   * Default assumed: 1000ms if not provided.
   */
  readonly tickIntervalMs?: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface FlashpointDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    /**
     * Query a tree-wide passive accumulator. Smothered (choke_passive) registers
     * `smokeCloudDurationBonus` under treeId `'fire'` with `'sum'` aggregation.
     */
    getTreePassiveEffect(treeId: string, key: string, aggregation: 'sum' | 'product'): number;
  };

  /**
   * Find the nearest zone whose type is one of `types`, within `maxTiles`
   * of the given world position. Returns null if none qualify.
   */
  readonly findNearestDetonatableZone: (
    x: number,
    y: number,
    types: readonly string[],
    maxTiles: number,
  ) => FlashpointZoneInfo | null;

  readonly zones: {
    remove(id: string): void;
    create(config: BackdraftZoneCreateConfig): string;
  };

  /** Return entity IDs of all enemies within `radiusTiles` of (x, y). */
  readonly getEnemiesInRadius: (x: number, y: number, radiusTiles: number) => string[];

  /** Deal direct damage to a target. */
  readonly dealDamage: (entityId: string, damage: number) => void;

  /** Push `entityId` away from the explosion centre by `tiles`. */
  readonly applyKnockback: (fromX: number, fromY: number, entityId: string, tiles: number) => void;

  /** Apply a status effect to an enemy entity. */
  readonly applyEnemyStatus: (
    entityId: string,
    statusId: string,
    opts: { durationMs: number },
  ) => void;

  /** True if the entity is currently within a CC immunity window for statusId. */
  readonly isImmune: (entityId: string, statusId: string) => boolean;

  /**
   * True if the given enemy currently has the specified status active.
   * Used by Chain Ignition to check for burning before refreshing.
   */
  readonly hasEnemyStatus: (entityId: string, statusId: string) => boolean;

  /**
   * Refresh a burning DoT on an enemy to full duration without increasing stacks.
   * Called by Chain Ignition with sourceSkillId 'chain_ignition'.
   * The `intensity` stackingRule on burning means re-application refreshes
   * duration only — stacks are not incremented.
   */
  readonly refreshEnemyBurning: (
    entityId: string,
    opts: { durationMs: number; sourceSkillId: string },
  ) => void;

  /** Return the player's current world position. */
  readonly getPlayerPosition: () => { x: number; y: number };

  /** Break stealth / hide state on activation. */
  readonly breakHide: () => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface FlashpointConfig {
  /** Cooldown in ms after any activation (including silent-fail). */
  readonly cooldownMs: number;
  /** Maximum interaction range in tiles. */
  readonly interactionRangeTiles: number;
  /** Smoke cloud damage at age ratio 0 (fresh). */
  readonly damageLow: number;
  /** Smoke cloud damage at age ratio 1 (fully aged). */
  readonly damageHigh: number;
  /** Multiplier for fire zone detonation damage. spec: 1.5. */
  readonly fireZoneDamageMultiplier: number;
  /** Default tick interval (ms) assumed for fire zones that omit tickIntervalMs. */
  readonly defaultFireZoneTickIntervalMs: number;
  /**
   * Full burning duration (ms) applied by Chain Ignition refresh.
   * Matches the default burning duration from status-effects.json (3000ms).
   */
  readonly defaultBurningDurationMs: number;
  /**
   * detectionReduction written into the post-detonation smoke cloud
   * created by Secondary Burn. Matches the Smoke passive's cloud value (0.3).
   */
  readonly postDetonationSmokeDetectionReduction: number;
}

const DEFAULT_CONFIG: FlashpointConfig = {
  cooldownMs: 1000,                          // "medium" cooldown tier
  interactionRangeTiles: 2,
  damageLow: 10,
  damageHigh: 25,
  fireZoneDamageMultiplier: 1.5,
  defaultFireZoneTickIntervalMs: 1000,
  defaultBurningDurationMs: 3000,
  postDetonationSmokeDetectionReduction: 0.3,
};

// ---------------------------------------------------------------------------
// Zone type constants
// ---------------------------------------------------------------------------

/** Zone types Flashpoint can detonate (both smoke and fire). */
const FLASHPOINT_ZONE_TYPES: readonly string[] = ['smoke_cloud', 'fire_zone'];

// ---------------------------------------------------------------------------
// Resolver key constants (all scoped to 'backdraft' — inherited upgrades)
// ---------------------------------------------------------------------------

const RESOLVER_SCOPE = 'backdraft';
const KEY_KNOCKBACK_TILES = 'knockbackTiles';
const KEY_STAGGER_DURATION_MS = 'staggerDurationMs';
const KEY_DETONATION_SMOKE_TILES = 'detonationSmokeTiles';
const KEY_DETONATION_SMOKE_DURATION_MS = 'detonationSmokeDurationMs';
const KEY_DETONATION_DOT_REFRESH = 'detonationDotRefresh';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class FlashpointSkillEffect implements SkillEffect {
  readonly skillId = 'flashpoint';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _silentFails = 0;

  constructor(
    private readonly _deps: FlashpointDeps,
    private readonly _config: FlashpointConfig = DEFAULT_CONFIG,
  ) {}

  // --- Accessors ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get silentFails(): number { return this._silentFails; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._deps.breakHide();

    const pos = this._deps.getPlayerPosition();

    // Find nearest detonatable zone — smoke cloud OR fire zone
    const zone = this._deps.findNearestDetonatableZone(
      pos.x,
      pos.y,
      FLASHPOINT_ZONE_TYPES,
      this._config.interactionRangeTiles,
    );

    // Cooldown always consumed — even on silent fail
    this._cooldownRemaining = this._config.cooldownMs;

    if (!zone) {
      this._silentFails++;
      return false;
    }

    // Compute damage based on zone type
    const damage = this._computeDamage(zone);

    // Blast: damage all enemies in zone's radius
    const enemies = this._deps.getEnemiesInRadius(
      zone.position.x,
      zone.position.y,
      zone.radiusTiles,
    );

    for (const enemyId of enemies) {
      this._deps.dealDamage(enemyId, damage);
    }

    // Inherited Overpressure: knockback away from explosion centre
    const knockbackBonus = this._deps.resolver.getAdditiveEffect(
      RESOLVER_SCOPE,
      KEY_KNOCKBACK_TILES,
    );
    if (knockbackBonus > 0) {
      for (const enemyId of enemies) {
        this._deps.applyKnockback(
          zone.position.x,
          zone.position.y,
          enemyId,
          knockbackBonus,
        );
      }
    }

    // Inherited Overpressure: stagger enemies (respects CC immunity)
    const staggerMs = this._deps.resolver.getAdditiveEffect(
      RESOLVER_SCOPE,
      KEY_STAGGER_DURATION_MS,
    );
    if (staggerMs > 0) {
      for (const enemyId of enemies) {
        if (!this._deps.isImmune(enemyId, 'stagger')) {
          this._deps.applyEnemyStatus(enemyId, 'stagger', { durationMs: staggerMs });
        }
      }
    }

    // Chain Ignition: refresh burning on each enemy in blast that is already burning
    if (this._deps.resolver.hasEffect(RESOLVER_SCOPE, KEY_DETONATION_DOT_REFRESH)) {
      for (const enemyId of enemies) {
        if (this._deps.hasEnemyStatus(enemyId, 'burning')) {
          this._deps.refreshEnemyBurning(enemyId, {
            durationMs: this._config.defaultBurningDurationMs,
            sourceSkillId: 'chain_ignition',
          });
        }
      }
    }

    // Remove the detonated zone
    this._deps.zones.remove(zone.id);

    // Inherited Secondary Burn: spawn a fresh smoke cloud at the detonation site
    if (this._deps.resolver.hasEffect(RESOLVER_SCOPE, KEY_DETONATION_SMOKE_TILES)) {
      const smokeRadius = this._deps.resolver.getAdditiveEffect(
        RESOLVER_SCOPE,
        KEY_DETONATION_SMOKE_TILES,
      );
      const baseDurationMs = this._deps.resolver.getAdditiveEffect(
        RESOLVER_SCOPE,
        KEY_DETONATION_SMOKE_DURATION_MS,
      );
      const smotheredBonusMs = this._deps.resolver.getTreePassiveEffect(
        'fire',
        'smokeCloudDurationBonus',
        'sum',
      );
      this._deps.zones.create({
        id: `flashpoint_secondary_smoke_${Date.now()}`,
        type: 'smoke_cloud',
        position: { x: zone.position.x, y: zone.position.y },
        shape: 'circle',
        radiusTiles: smokeRadius,
        durationMs: baseDurationMs + smotheredBonusMs,
        detectionReduction: this._config.postDetonationSmokeDetectionReduction,
        sourceSkillId: 'backdraft_secondary',
      });
    }

    this._activations++;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Damage formula (zone-type-dependent)
  // ---------------------------------------------------------------------------

  /**
   * Compute detonation damage for the given zone.
   *
   * - smoke_cloud: `damageLow + (damageHigh - damageLow) × ageRatio`
   * - fire_zone:   `dotIntensity × remainingTicks × 1.5`
   *   where remainingTicks = Math.floor(remainingMs / tickIntervalMs)
   */
  private _computeDamage(zone: FlashpointZoneInfo): number {
    if (zone.type === 'fire_zone') {
      const dotIntensity = zone.dotIntensity ?? 0;
      const tickIntervalMs = zone.tickIntervalMs ?? this._config.defaultFireZoneTickIntervalMs;
      const remainingMs = zone.durationMs - zone.elapsedMs;
      const remainingTicks = Math.floor(remainingMs / tickIntervalMs);
      return dotIntensity * remainingTicks * this._config.fireZoneDamageMultiplier;
    }

    // smoke_cloud (or any other zone type): age-scaled formula
    const ageRatio = Math.min(zone.elapsedMs / zone.durationMs, 1.0);
    return this._config.damageLow
      + (this._config.damageHigh - this._config.damageLow) * ageRatio;
  }
}
